<template>
  <transition
    name="toast"
    enter-active-class="dnl:transition-opacity dnl:duration-500 dnl:ease-linear"
    enter-from-class="dnl:opacity-0"
    leave-active-class="dnl:transition-opacity dnl:duration-500 dnl:ease-linear"
    leave-to-class="dnl:opacity-0"
  >
    <div
      v-if="toastProps.visible"
      :class="[
        'dnl:fixed dnl:px-4 dnl:py-2 dnl:rounded-lg dnl:shadow-lg dnl:flex dnl:items-center dnl:z-[1000] dnl:border',
        backgroundClass,
        borderClass,
        positionClass,
      ]"
      :data-testId="toastProps.testId"
    >
      <svg
        v-if="toastProps.icon"
        class="dnl:mr-2 dnl:shrink-0"
        :class="[
          `dnl:w-${toastProps.iconSize} dnl:h-${toastProps.iconSize}`,
          textClass,
          toastProps.iconClass,
        ]"
      >
        <use :href="`/img/icons/icons.svg#${toastProps.icon}`"></use>
      </svg>
      <span
        v-if="toastProps.text"
        :class="[textClass, toastProps.contentClass]"
        class="dnl:text-xs dnl:flex-grow dnl:font-semibold"
        >{{ $t(toastProps.text) }}</span
      >
      <button
        v-if="toastProps.showRetry && toastProps.onRetry"
        class="dnl:ml-3 dnl:p-1 dnl:rounded dnl:hover:bg-black/10 dnl:focus:outline-none dnl:focus:ring-2 dnl:focus:ring-black/20 dnl:shrink-0"
        :aria-label="$t('actions.retry')"
        @click="toastProps.onRetry?.()"
      >
        <svg
          class="dnl:w-5 dnl:h-5"
          :class="textClass"
        >
          <use href="/img/icons/icons.svg#sync"></use>
        </svg>
      </button>
    </div>
  </transition>
</template>

<script setup lang="ts">
  import { computed, watch } from 'vue';
  import { toastProps } from '@/services/toastService';

  let timer: ReturnType<typeof setTimeout> | null = null;

  const stateColors = {
    progress: {
      bg: 'dnl:bg-amber-50',
      text: 'dnl:text-amber-600',
      border: 'dnl:border-amber-200',
    },
    success: {
      bg: 'dnl:bg-green-50',
      text: 'dnl:text-green-600',
      border: 'dnl:border-green-200',
    },
    error: {
      bg: 'dnl:bg-red-50',
      text: 'dnl:text-red-600',
      border: 'dnl:border-red-200',
    },
  };

  const backgroundClass = computed(() => {
    if (toastProps.state && stateColors[toastProps.state]) {
      return stateColors[toastProps.state].bg;
    }
    return `dnl:bg-${toastProps.bgColor}`;
  });

  const textClass = computed(() => {
    if (toastProps.state && stateColors[toastProps.state]) {
      return stateColors[toastProps.state].text;
    }
    return `dnl:text-${toastProps.color}`;
  });

  const borderClass = computed(() => {
    if (toastProps.state && stateColors[toastProps.state]) {
      return stateColors[toastProps.state].border;
    }
    return `dnl:border-${toastProps.borderColor}`;
  });

  const positionClass = computed(() => {
    switch (toastProps.position) {
      case 'top-left':
        return 'dnl:top-5 dnl:left-5';
      case 'top-right':
        return 'dnl:top-5 dnl:right-5';
      case 'bottom-left':
        return 'dnl:bottom-5 dnl:left-5';
      case 'bottom-right':
      default:
        return 'dnl:bottom-5 dnl:right-5';
    }
  });

  const show = () => {
    toastProps.visible = true;

    // Only set timeout if duration is not 0 (0 means sticky)
    if (toastProps.duration && toastProps.duration > 0) {
      clearTimeout(timer as ReturnType<typeof setTimeout>);
      timer = setTimeout(() => {
        toastProps.visible = false;
      }, toastProps.duration);
    }
  };

  // Combined watcher for toast properties to manage visibility and auto-hide.
  watch(
    () => [toastProps.text, toastProps.testId, toastProps.visible, toastProps.duration],
    (
      [newText, newTestId, newVisible, newDuration],
      [oldText, oldTestId, oldVisible, oldDuration],
    ) => {
      if (newVisible) {
        // If the toast should be visible, call show() if:
        // - It just became visible OR
        // - Its content (text, testId) changed OR
        // - Its duration changed.
        // The show() function handles setting visible = true and managing the timer.
        if (
          !oldVisible ||
          newText !== oldText ||
          newTestId !== oldTestId ||
          newDuration !== oldDuration
        ) {
          show();
        }
      } else if (!newVisible && oldVisible) {
        // If the toast became invisible (either programmatically or by its own timer),
        // ensure any potentially active timer is cleared.
        clearTimeout(timer as ReturnType<typeof setTimeout>);
      }
    },
  );
</script>
